// React
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Redux
import { useDispatch } from "react-redux";
import { openSnackbar } from "Store/SnackbarSlice";

// Material UI
import { Card, Link, Stack, Typography } from "@mui/material";

// Auth
import { useMBSAuth } from "Auth/MBS/MBSAuthContext";
import {
  clearPendingAuthMessage,
  getPendingAuthMessage,
} from "Auth/MBS/mbsStorage";

// Constants
import { MBS_LOGIN_MODE, MBS_AUTH_ERROR_CODES } from "Constants/AuthConstants";

// Components
import MBSForgotPasswordForm from "./MBSForgotPasswordForm";
import MBSLoginForm from "./MBSLoginForm";
import MBSNewPasswordForm from "./MBSNewPasswordForm";
import MBSSSOForm from "./MBSSSOForm";
import { styles } from "./styles";

const MBSLoginPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signIn,
    signInWithSso,
    confirmNewPassword,
    forgotPassword,
    confirmForgotPassword,
  } = useMBSAuth();
  const returnTo = location.state?.from || "/mbs/home";

  const [mode, setMode] = useState(MBS_LOGIN_MODE.SSO);
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");

  useEffect(() => {
    const pendingAuthMessage = getPendingAuthMessage();
    if (!pendingAuthMessage.message) {
      return;
    }

    const isPendingError = pendingAuthMessage.severity === "error";
    dispatch(
      openSnackbar({
        message: isPendingError
          ? t("mbsAuth.errors.accountVerificationError")
          : pendingAuthMessage.message,
        description: isPendingError ? pendingAuthMessage.message : "",
        severity: pendingAuthMessage.severity,
        keepOpen: isPendingError,
      })
    );

    const markShownTimeout = setTimeout(() => {
      clearPendingAuthMessage();
    }, 1000);
    return () => {
      clearTimeout(markShownTimeout);
    };
  }, [dispatch, t]);

  const fadeInForm =
    mode === MBS_LOGIN_MODE.SSO || mode === MBS_LOGIN_MODE.LOGIN;

  const handleSignInSubmit = async (userEmail, password) => {
    try {
      const result = await signIn(userEmail, password);
      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        setTemporaryPassword(password);
        setMode(MBS_LOGIN_MODE.NEW_PASSWORD);
        return;
      }
      if (result.challengeName === "RESET_PASSWORD") {
        setTemporaryPassword("");
        setForgotPasswordEmail(userEmail);
        setMode(MBS_LOGIN_MODE.FORGOT_PASSWORD);
        dispatch(
          openSnackbar({
            message: t("signIn.adminPasswordResetRequested"),
            severity: "warning",
          })
        );
        return;
      }
      navigate(returnTo, { replace: true });
    } catch (signInError) {
      setTemporaryPassword("");
      dispatch(
        openSnackbar({
          message: signInError.message || t("mbsAuth.errors.generic"),
          severity: "error",
        })
      );
    }
  };

  const handleNewPasswordSubmit = async (newPassword, userAttributes) => {
    try {
      await confirmNewPassword(newPassword, userAttributes);
      dispatch(
        openSnackbar({
          message: t("mbsAuth.feedback.passwordUpdated"),
          severity: "success",
        })
      );
      navigate(returnTo, { replace: true });
    } catch (newPasswordError) {
      if (
        newPasswordError.code === MBS_AUTH_ERROR_CODES.EXPIRED_CHALLENGE_SESSION
      ) {
        setTemporaryPassword("");
        setMode(MBS_LOGIN_MODE.LOGIN);
      }
      dispatch(
        openSnackbar({
          message: newPasswordError.message || t("mbsAuth.errors.generic"),
          severity: "error",
        })
      );
    }
  };

  const handleSsoSubmit = async (userEmail) => {
    try {
      await signInWithSso(userEmail);
    } catch (ssoError) {
      dispatch(
        openSnackbar({
          message: ssoError.message || t("mbsAuth.errors.generic"),
          severity: "error",
        })
      );
    }
  };

  const handleForgotPassword = () => {
    setTemporaryPassword("");
    setForgotPasswordEmail("");
    setMode(MBS_LOGIN_MODE.FORGOT_PASSWORD);
  };

  const handleForgotPasswordCodeRequest = async (userEmail) => {
    await forgotPassword(userEmail);
    dispatch(
      openSnackbar({
        message: t("mbsAuth.feedback.verificationCodeSent"),
        severity: "success",
      })
    );
  };

  const handleForgotPasswordSubmit = async (userEmail, code, newPassword) => {
    await confirmForgotPassword(userEmail, code, newPassword);
  };

  const handleBackToSignIn = () => {
    setTemporaryPassword("");
    setForgotPasswordEmail("");
    setMode(MBS_LOGIN_MODE.LOGIN);
  };

  const renderAuthForm = () => {
    switch (mode) {
      case MBS_LOGIN_MODE.SSO:
        return (
          <MBSSSOForm
            onSubmit={handleSsoSubmit}
            onSignInWithEmail={() => setMode(MBS_LOGIN_MODE.LOGIN)}
          />
        );

      case MBS_LOGIN_MODE.LOGIN:
        return (
          <MBSLoginForm
            onSubmit={handleSignInSubmit}
            onForgotPassword={handleForgotPassword}
            onSignInWithSso={() => setMode(MBS_LOGIN_MODE.SSO)}
          />
        );

      case MBS_LOGIN_MODE.NEW_PASSWORD:
        return (
          <MBSNewPasswordForm
            onSubmit={handleNewPasswordSubmit}
            temporaryPassword={temporaryPassword}
            onBackToSignIn={() => {
              setTemporaryPassword("");
              setMode(MBS_LOGIN_MODE.LOGIN);
            }}
          />
        );

      case MBS_LOGIN_MODE.FORGOT_PASSWORD:
        return (
          <MBSForgotPasswordForm
            initialEmail={forgotPasswordEmail}
            onRequestCode={handleForgotPasswordCodeRequest}
            onSubmit={handleForgotPasswordSubmit}
            onBackToSignIn={handleBackToSignIn}
          />
        );

      default:
        return null;
    }
  };

  return (
    <Stack sx={styles.page}>
      <Stack spacing={2} alignItems="center" sx={styles.content}>
        <Card sx={styles.card}>
          {fadeInForm ? (
            <Stack key={mode} sx={styles.authFormFadeIn}>
              {renderAuthForm()}
            </Stack>
          ) : (
            renderAuthForm()
          )}
        </Card>
        <Typography variant="body2" textAlign="center">
          {t("footer.techSupportPrompt")}
          <br />
          <Link href="mailto:techsupport@medinav.ca" sx={styles.footerLink}>
            techsupport@medinav.ca
          </Link>
        </Typography>
      </Stack>
    </Stack>
  );
};

export default MBSLoginPage;
